/**
 * Typed port of `design/games-data.js` plus the prototype's inline seeds
 * (`ptSeed()` line 1944, and the GOTY potentials/tiers block at line 1415).
 *
 * This is the first-run payload for `LocalRepository`. In production the same
 * catalog ships as the `20260808_1400_seed_catalog.sql` migration and the crew
 * rows as `supabase/seed/demo-crew/` (ARCHITECTURE §1.1) — this module is the
 * dev-parity twin of both.
 *
 * DEVIATIONS
 *  - Seed passwords are `START123`, not `START`: PORTING-NOTES D-01 raises the
 *    minimum to 8 characters. Chelsea still carries `mustReset`.
 *  - Passwords are not a field on `User`. Supabase Auth owns credentials
 *    (ARCHITECTURE §1.3: "Prototype `u.pw` has no column"), so LocalRepository
 *    keeps them in a separate map that never reaches the store.
 *  - Every absolute timestamp is built with `Date.UTC`. The prototype used
 *    `+new Date('2026-09-15T12:00:00')`, which parses in the *machine's* time
 *    zone and would make a committed selector fixture machine-dependent.
 */

import { ptPartyLbl } from '../lib/format.ts';
import { sanitizeVotes } from '../lib/logic.ts';
import type {
  BacklogEntry,
  BoardEvent,
  Game,
  ListEntry,
  Playtest,
  ScoreRec,
  SendRec,
  TriggerKey,
  WatchRec,
  Tier,
  User,
} from '../lib/types.ts';

// ---------------------------------------------------------------------------
// catalog
// ---------------------------------------------------------------------------

/**
 * `[appid, name, dev, 'YYYY-MM'|'TBA', price(null=TBA,0=free), oldPrice,
 *   minPl, maxPl, platforms, tags, reviewPct, tier, peakCCU, upcoming, desc,
 *   earlyAccess, hasDemo, demoAppid, artUrl]`
 */
export type PackedGameRow = readonly [
  number, string, string, string, number | null, number, number, number, string, string,
  number, string, number, number, string, ...(number | string)[],
];

export const SEED_GAME_ROWS: readonly PackedGameRow[] = [
  [1966720, 'Lethal Company', 'Zeekerss', '2023-10', 9.99, 0, 1, 4, 'P', 'coop,horror,proc-gen', 98, 'op', 240000, 0, 'Scrap-runs on abandoned moons to hit the company quota. Proximity voice makes it.'],
  [3241660, 'R.E.P.O.', 'semiwork', '2025-02', 9.99, 0, 1, 6, 'P', 'coop,horror,physics', 97, 'op', 220000, 0, 'Physics-horror extraction: haul fragile valuables out of haunted buildings.', 1],
  [553850, 'Helldivers 2', 'Arrowhead Game Studios', '2024-02', 39.99, 0, 1, 4, 'PS', 'coop,shooter,pve', 91, 'vp', 458000, 0, 'Squad shooter for managed democracy. Friendly fire is always on.'],
  [548430, 'Deep Rock Galactic', 'Ghost Ship Games', '2020-05', 9.89, 29.99, 1, 4, 'PXS', 'coop,shooter,mining', 97, 'op', 112000, 0, 'Dwarves mine procedurally generated caves. Rock and stone.'],
  [892970, 'Valheim', 'Iron Gate AB', '2021-02', 19.99, 0, 1, 10, 'PX', 'coop,survival,crafting', 96, 'op', 502000, 0, 'Viking survival across brutal biomes; base building and boss hunts.', 1],
  [1086940, "Baldur's Gate 3", 'Larian Studios', '2023-08', 59.99, 0, 1, 4, 'PXS', 'coop,rpg,turn-based', 96, 'op', 875000, 0, 'A full D&D campaign in co-op. One run takes a season; worth it.'],
  [1326470, 'Sons of the Forest', 'Endnight Games', '2024-02', 28.99, 0, 1, 8, 'P', 'coop,survival,horror', 89, 'vp', 414000, 0, 'Cannibal-island survival with base building and a very creepy spring.'],
  [1623730, 'Palworld', 'Pocketpair', '2024-01', 22.49, 29.99, 1, 32, 'PX', 'coop,survival,crafting,creatures', 93, 'vp', 2100000, 0, 'Creature-collecting survival crafting on a huge shared island.'],
  [2881650, 'Content Warning', 'Landfall', '2024-04', 7.99, 0, 1, 4, 'P', 'coop,horror,comedy', 95, 'vp', 205000, 0, 'Film scary things with your friends, go viral on SpookTube.'],
  [1621690, 'Core Keeper', 'Pugstorm', '2024-08', 19.99, 0, 1, 8, 'PXSN', 'coop,sandbox,mining', 92, 'vp', 68000, 0, 'Cozy underground sandbox: dig, farm, fight titans.'],
  [1203620, 'Enshrouded', 'Keen Games', '2024-01', 29.99, 0, 1, 16, 'P', 'coop,survival,rpg', 90, 'vp', 161000, 0, 'Voxel survival ARPG with a big shared world and building.', 1],
  [602960, 'Barotrauma', 'Undertow Games', '2023-03', 34.99, 0, 1, 16, 'P', 'coop,sim,horror', 94, 'vp', 25000, 0, 'Crew a submarine under the ice of Europa. Chaos guaranteed.'],
  [322330, "Don't Starve Together", 'Klei Entertainment', '2016-04', 14.99, 0, 1, 6, 'PXS', 'coop,survival,crafting', 96, 'op', 45000, 0, 'Gothic wilderness survival; winter will kill you first.'],
  [739630, 'Phasmophobia', 'Kinetic Games', '2020-09', 19.99, 0, 1, 4, 'PXS', 'coop,horror,investigation', 96, 'op', 120000, 0, 'Ghost-hunting contracts with voice-reactive spirits.'],
  [105600, 'Terraria', 'Re-Logic', '2011-05', 9.99, 0, 1, 8, 'PXSN', 'coop,sandbox,crafting', 97, 'op', 250000, 0, '2D sandbox with endless bosses and biomes. Still updated.'],
  [413150, 'Stardew Valley', 'ConcernedApe', '2016-02', 14.99, 0, 1, 8, 'PXSN', 'coop,farming,sim', 98, 'op', 236000, 0, 'Farm co-op up to 8; the comfiest game on this list.'],
  [252490, 'Rust', 'Facepunch Studios', '2018-02', 39.99, 0, 1, 100, 'PXS', 'pvp,survival,crafting', 87, 'vp', 245000, 0, 'Ruthless multiplayer survival. Everything is a threat, mostly players.'],
  [108600, 'Project Zomboid', 'The Indie Stone', '2013-11', 19.99, 0, 1, 32, 'P', 'coop,survival,zombies', 93, 'vp', 65000, 0, 'This is how you died: hardcore zombie survival sandbox.', 1],
  [526870, 'Satisfactory', 'Coffee Stain Studios', '2024-09', 39.99, 0, 1, 4, 'P', 'coop,factory,building', 97, 'op', 190000, 0, 'First-person factory building on an alien planet.'],
  [427520, 'Factorio', 'Wube Software', '2020-08', 35, 0, 1, 65, 'PN', 'coop,factory,automation', 96, 'op', 89000, 0, 'The factory must grow. Space Age doubled it.'],
  [648800, 'Raft', 'Redbeet Interactive', '2022-06', 19.99, 0, 1, 8, 'P', 'coop,survival,ocean', 93, 'vp', 68000, 0, 'Survive the ocean on a growing raft; shark included.'],
  [620, 'Portal 2', 'Valve', '2011-04', 9.99, 0, 1, 2, 'PX', 'coop,puzzle', 98, 'op', 25000, 0, 'Two-player puzzle campaign that still has no equal.'],
  [4000, "Garry's Mod", 'Facepunch Studios', '2006-11', 9.99, 0, 1, 128, 'P', 'sandbox,comedy,coop', 96, 'op', 90000, 0, 'Physics sandbox with two decades of community game modes.'],
  [945360, 'Among Us', 'Innersloth', '2018-11', 4.99, 0, 4, 15, 'PXSN', 'party,social-deduction', 92, 'vp', 447000, 0, 'Find the impostor. Ruin friendships. Repeat.'],
  [2073850, 'THE FINALS', 'Embark Studios', '2023-12', 0, 0, 1, 3, 'PXS', 'pvp,shooter,destruction', 87, 'vp', 242000, 0, 'Free team shooter in fully destructible arenas.'],
  [2767030, 'Marvel Rivals', 'NetEase Games', '2024-12', 0, 0, 1, 6, 'PXS', 'pvp,hero-shooter', 88, 'vp', 644000, 0, 'Free 6v6 hero shooter with destructible maps.'],
  [3164500, 'Schedule I', 'TVGS', '2025-03', 19.99, 0, 1, 4, 'P', 'coop,sim,crime', 97, 'op', 459000, 0, 'Co-op small-business sim, if the business were extremely illegal.'],
  [3883570, 'Invokyr', 'Ludogram', 'TBA', null, 0, 1, 4, 'P', 'coop,horror,board-game,psychological', 93, 'vp', 0, 1, 'Trapped in a manor by a cursed 70s board game: roll dice, survive the Gamemaster, escape. Free demo out now — 94% of demo reviews are positive.', 0, 1, 4704030, 'https://shared.cloudflare.steamstatic.com/store_item_assets/steam/apps/3883570/2f47e8044af8208d7c5c6c91a865f7b84941d316/library_capsule.jpg?t=1783693735'],
  [3527290, 'PEAK', 'Aggro Crab / Landfall', '2025-06', 7.99, 0, 1, 4, 'P', 'coop,climbing,physics', 95, 'vp', 130000, 0, 'Co-op mountain climbing; one slip takes the whole rope team.'],
  [1030300, 'Hollow Knight: Silksong', 'Team Cherry', '2025-09', 19.99, 0, 1, 1, 'PXSN', 'metroidvania,platformer', 95, 'op', 535000, 0, 'The sequel finally happened. Solo, but it earns a spot.'],
  [2246340, 'Monster Hunter Wilds', 'Capcom', '2025-02', 69.99, 0, 1, 4, 'PXS', 'coop,action,hunting', 60, 'mx', 1380000, 0, 'Hunt giant monsters in squads; PC port still divisive.'],
  [1145350, 'Hades II', 'Supergiant Games', '2024-05', 29.99, 0, 1, 1, 'PN', 'roguelite,action', 96, 'op', 103000, 0, "Supergiant's roguelite; solo, but great between sessions."],
  [1794680, 'Vampire Survivors', 'poncle', '2022-10', 4.99, 0, 1, 4, 'PXSN', 'roguelite,coop,bullet-heaven', 98, 'op', 76000, 0, 'Reverse bullet-hell snack; online co-op up to 4.'],
  [1942280, 'Brotato', 'Blobfish', '2023-06', 4.99, 0, 1, 4, 'PXSN', 'roguelite,coop', 97, 'op', 28000, 0, 'A potato with six weapons. Quick co-op runs.'],
  [1085660, 'Destiny 2', 'Bungie', '2019-10', 0, 0, 1, 6, 'PXS', 'coop,looter-shooter,mmo', 78, 'mp', 316000, 0, 'Free-to-start looter with raids for a full fireteam.'],
  [1172470, 'Apex Legends', 'Respawn', '2020-11', 0, 0, 1, 3, 'PXSN', 'pvp,battle-royale', 79, 'mp', 624000, 0, 'Trio battle royale with the best movement in the genre.'],
  [1466860, 'Age of Empires IV', "Relic / World's Edge", '2021-10', 39.99, 0, 1, 8, 'PX', 'strategy,rts,coop', 86, 'vp', 73000, 0, 'Classic RTS; co-op vs AI is a great crew night.'],
  [1245620, 'Elden Ring', 'FromSoftware', '2022-02', 59.99, 0, 1, 4, 'PXS', 'coop,souls,open-world', 92, 'vp', 953000, 0, 'Open-world souls; seamless co-op via mods.'],
  [2622380, 'Elden Ring Nightreign', 'FromSoftware', '2025-05', 39.99, 0, 1, 3, 'PXS', 'coop,souls,roguelite', 72, 'mp', 313000, 0, 'Standalone 3-player run-based Elden Ring.'],
  [2183900, 'Warhammer 40,000: Space Marine 2', 'Saber Interactive', '2024-09', 59.99, 0, 1, 3, 'PXS', 'coop,action,horde', 90, 'vp', 225000, 0, 'Purge tyranid hordes in 3-player operations.'],
  [250900, 'The Binding of Isaac: Rebirth', 'Nicalis', '2014-11', 14.99, 0, 1, 4, 'PXSN', 'roguelite,coop', 97, 'op', 40000, 0, 'The roguelite classic; online co-op now built in.'],
  [2001120, 'Split Fiction', 'Hazelight Studios', '2025-03', 49.99, 0, 1, 2, 'PXS', 'coop,adventure,split-screen', 96, 'op', 259000, 0, 'Two-player co-op adventure from the It Takes Two studio.'],
  [1426210, 'It Takes Two', 'Hazelight Studios', '2021-03', 39.99, 0, 1, 2, 'PXSN', 'coop,adventure', 96, 'op', 64000, 0, 'Mandatory two-player co-op with a friend pass.'],
  [1808500, 'ARC Raiders', 'Embark Studios', '2025-10', 39.99, 0, 1, 3, 'PXS', 'pvp,extraction,shooter', 90, 'vp', 355000, 0, 'Extraction shooter vs machines and other squads.'],
  [1962700, 'Subnautica 2', 'Unknown Worlds', '2026-05', 29.99, 0, 1, 4, 'PX', 'coop,survival,ocean', 93, 'vp', 0, 0, 'Co-op deep-sea survival on a new planet. In early access since May 2026.', 1],
  [1422450, 'Deadlock', 'Valve', 'TBA', null, 0, 1, 12, 'P', 'pvp,moba,shooter', 0, '', 0, 1, "Valve's 6v6 shooter-MOBA, still in invite-only playtest."],
];

const asNum = (v: number | string | undefined): number => (typeof v === 'number' ? v : 0);
const asStr = (v: number | string | undefined): string => (typeof v === 'string' ? v : '');

/** Prototype `cat()` unpacking, line 1429. */
export function unpackGame(r: PackedGameRow): Game {
  return {
    id: r[0],
    appid: r[0],
    n: r[1],
    dev: r[2],
    rd: r[3],
    p: r[4],
    o: r[5],
    mn: r[6],
    mx: r[7],
    plat: r[8],
    tags: r[9] ? r[9].split(',') : [],
    rp: r[10],
    pk: r[12],
    // The seed carries no live sample; `popOf` falls back to `pk`.
    ccu: 0,
    up: !!r[13],
    ds: r[14],
    ea: !!asNum(r[15]),
    demo: !!asNum(r[16]),
    dAid: asNum(r[17]),
    art: asStr(r[18]),
    live: false,
    thumb: '',
  };
}

/** A fresh copy of the bundled catalog (46 rows). */
export function seedCatalog(): Game[] {
  return SEED_GAME_ROWS.map(unpackGame);
}

// ---------------------------------------------------------------------------
// crew
// ---------------------------------------------------------------------------

/** `[key, name, role, hue, email, password, mustReset]` */
type PackedUserRow = readonly [string, string, 'admin' | 'user', number, string, string, 0 | 1];

export const SEED_USER_ROWS: readonly PackedUserRow[] = [
  ['marcus', 'Marcus', 'admin', 150, 'marcus@example.com', 'START123', 0],
  ['chris', 'Chris', 'user', 250, '', 'START123', 0],
  ['dave', 'Dave', 'user', 40, '', 'START123', 0],
  ['jeff', 'Jeff', 'user', 320, '', 'START123', 0],
  ['mike', 'Mike', 'user', 200, '', 'START123', 0],
  ['chelsea', 'Chelsea', 'user', 90, '', 'START123', 1],
];

/** Seven days, the prototype's `lastRev` offset (line 1401). */
export const LAST_REV_OFFSET_MS = 7 * 864e5;

export function seedUsers(now: number): User[] {
  return SEED_USER_ROWS.map((r) => ({
    k: r[0],
    name: r[1],
    role: r[2],
    hue: r[3],
    // No seeded custom colors: the strip's tiles and the ALREADY WORN row
    // exercise the hue path; a custom color is a member's own act.
    col: '',
    email: r[4],
    // The seed models the population that PREDATES email: every account signs
    // in at the synthesized domain, which `loginEmail` reports as `''` (see the
    // field, and migration 3029). That is not a gap in the fixture — it is the
    // state MANAGE CREW has to render honestly, and the one Marcus is seeding
    // his way out of.
    loginEmail: '',
    mustReset: !!r[6],
    av: '',
    lastRev: now - LAST_REV_OFFSET_MS,
    seen: [],
  }));
}

/** userKey → plaintext password. Dev parity only; never leaves the repository. */
export function seedCredentials(): Record<string, string> {
  const out: Record<string, string> = {};
  for (const r of SEED_USER_ROWS) out[r[0]] = r[5];
  return out;
}

// ---------------------------------------------------------------------------
// shared list
// ---------------------------------------------------------------------------

/**
 * `[appid, addedByKey, daysAgo, votes{key: s|a|b|c}, comments[[byKey, daysAgo,
 *   text]]]`
 */
type PackedListRow = readonly [
  number,
  string,
  number,
  Readonly<Record<string, string>>,
  ReadonlyArray<readonly [string, number, string]>,
];

/**
 * The calls are `games-data.js`'s own 4-tier spread, copied across rather than
 * migrated: upstream re-authored the binary seed by hand, so the old ▲s came
 * back as a mix of `s`/`a`/`b` (docs/porting/V.md §5). Putting the `y→a`/`n→c`
 * map over the previous rows instead yields only `a` and `c`, which files four
 * of the six games on the same A row and prints `AVG 2.0` on five of them —
 * a demo board that demonstrates nothing the widening was for.
 */
export const SEED_LIST_ROWS: readonly PackedListRow[] = [
  [1966720, 'chris', 16, { marcus: 's', dave: 's', jeff: 'a', chris: 's', mike: 'a', chelsea: 's' }, [
    ['marcus', 14, 'MoreCompany mod bumps the lobby to 8+ and it just works: https://thunderstore.io/c/lethal-company/p/notnotnotswipez/MoreCompany/'],
    ['chris', 16, 'Quota never ends. Perfect with a full crew and proximity voice.'],
  ]],
  [553850, 'dave', 9, { dave: 's', mike: 'a' }, [
    ['chelsea', 1, 'Crossplay works if anyone is on PS5. The new warbond is supposedly good.'],
  ]],
  [892970, 'marcus', 30, { marcus: 'a', chelsea: 's', mike: 'b', chris: 'a' }, [
    ['marcus', 5, 'I can host a dedicated server so progress does not depend on one of us being online.'],
  ]],
  [3241660, 'chelsea', 6, { chelsea: 's', jeff: 'a', marcus: 'a', chris: 'b', dave: 'a' }, []],
  [548430, 'mike', 21, { mike: 'a', chelsea: 'b', marcus: 'b' }, []],
  [602960, 'jeff', 3, { jeff: 'b', chris: 'c', dave: 'c' }, [
    ['jeff', 3, 'Sub-sim chaos. Needs at least 4 of us or we all drown in the first mission.'],
  ]],
];

/** LocalRepository's entry handle. Stable because the list is unique per game. */
export function localEntryId(appid: number | string): string {
  return 'le_' + appid;
}

export function seedList(now: number): ListEntry[] {
  return SEED_LIST_ROWS.map((r) => ({
    id: r[0],
    entryId: localEntryId(r[0]),
    by: r[1],
    at: now - r[2] * 864e5,
    votes: sanitizeVotes({ ...r[3] }),
    comments: r[4].map((c, i) => ({ id: r[0] + '-' + i, by: c[0], at: now - c[1] * 864e5, t: c[2] })),
  }));
}

// ---------------------------------------------------------------------------
// personal boards (prototype line 1415)
// ---------------------------------------------------------------------------

// CHRIS got seeded lists for this year and last (DESIGN-CHANGELOG 2026-09-03
// "A player's page": "so a player's page has something to browse and the year
// picker something to switch between"); last year's rides the `key@year` key
// (AA phase 5 §G). MARCUS's finished 2025 list, likewise.
export const SEED_POTENTIALS: Readonly<Record<string, number[]>> = {
  marcus: [2001120, 3241660, 3527290, 3164500, 2622380, 1030300],
  'marcus@2025': [1245620, 1086940, 413150],
  chris: [1030300, 427520, 1086940],
  'chris@2025': [1245620, 105600],
};

export const SEED_TIERS: Readonly<Record<string, Record<string, Tier>>> = {
  marcus: { '2001120': 'S', '3241660': 'A' },
  'marcus@2025': { '1245620': 'S', '1086940': 'A', '413150': 'B' },
  chris: { '1030300': 'S', '427520': 'A', '1086940': 'X' },
  'chris@2025': { '1245620': 'S', '105600': 'B' },
};

export const SEED_TIER_ORDER: Readonly<Record<string, number[]>> = {
  marcus: [2001120, 3241660],
  'marcus@2025': [1245620, 1086940, 413150],
  chris: [1030300, 427520, 1086940],
  'chris@2025': [1245620, 105600],
};

// ---------------------------------------------------------------------------
// my games (AA phase 2) — `design/gt-logic.js#mineSeed`, the rows whose games
// are in this catalog. Row: [appid, score | null, score days ago].
// ---------------------------------------------------------------------------

const SEED_MINE: Readonly<Record<string, ReadonlyArray<readonly [number, number | null, number]>>> = {
  marcus: [
    [1030300, 9, 20],
    [1145350, 8, 15],
    [108600, 7, 40],
    [413150, 10, 60],
    [105600, 8, 33],
    [1794680, 6, 11],
    [526870, null, 0],
  ],
  chris: [
    [427520, 10, 7],
    [1030300, 9, 2],
    [1086940, null, 0],
  ],
  mike: [
    [427520, 10, 7],
    [1145350, 9, 4],
  ],
  chelsea: [
    [2001120, 9, 9],
    [250900, 10, 5],
    [413150, 9, 12],
  ],
  jeff: [
    [2001120, 8, 9],
    [1245620, 9, 18],
  ],
  dave: [
    [1245620, 10, 21],
    [1145350, 8, 6],
    [620, null, 0],
  ],
};

/**
 * The design's seed sends (`gt-logic.js:574-586`): `[id, from, to, gid, note,
 * daysAgo]`. Notes in sentence case on purpose — it is a person talking. The
 * `c2` row (nina → marcus) is not here: the local double is one crew.
 */
const SEED_SENDS: ReadonlyArray<readonly [string, string, string, number, string, number]> = [
  ['s1', 'dave', 'marcus', 1245620, "Do it blind. Don't look anything up, don't ask me where to go.", 1],
  ['s2', 'chelsea', 'marcus', 250900, '600 hours in and I still find items I have never seen.', 3],
  ['s3', 'marcus', 'chris', 1030300, 'Pantheon-hard in places. You will love it.', 5],
  ['s4', 'jeff', 'dave', 2001120, 'The split-screen chapter alone is worth the price.', 6],
];

/**
 * The design's seed watches (`gt-logic.js:556-572`, the third column of
 * `mineSeed` plus its `extra`): per account, by game id. `cap` is the PRICE
 * floor in whole dollars; the default when a record has none is `capFor`'s.
 */
const SEED_WATCH: Readonly<Record<string, Readonly<Record<string, Partial<WatchRec>>>>> = {
  marcus: {
    '1030300': { upd: true, dlc: true },
    '1962700': { ea: true, date: true },
    '1145350': { dlc: true },
    '108600': { ea: true, upd: true },
    '413150': { upd: true },
    '105600': { price: true, cap: 6 },
    '1794680': { dlc: true },
    '526870': { sale: true, price: true, cap: 24 },
    '1966720': { upd: true },
    '3241660': { ea: true },
    '553850': { dlc: true, price: true, cap: 24 },
    '892970': { ea: true, upd: true },
    '1422450': { date: true, demo: true },
    '3883570': { date: true },
  },
  chris: { '1030300': { dlc: true }, '1086940': { dlc: true } },
  mike: { '1145350': { dlc: true } },
};

export function seedWatch(): Record<string, Record<string, WatchRec>> {
  const out: Record<string, Record<string, WatchRec>> = {};
  for (const [k, by] of Object.entries(SEED_WATCH)) {
    out[k] = {};
    for (const [gid, w] of Object.entries(by)) {
      out[k][gid] = { ea: false, date: false, price: false, sale: false, demo: false, upd: false, dlc: false, cap: 6, ...w };
    }
  }
  return out;
}

/**
 * The design's SEEDED world events (`gt-logic.js:540-554` `NB_SEED`), written
 * here as the rows the watcher would have written FOR EACH ACCOUNT whose
 * armed triggers let them through — the prototype's `nbKind` gate, applied at
 * seed time so the local board reads the table and nothing else, exactly as
 * the app's does (3063). `d` = days ago; `p` = the sale price.
 */
const NB_SEED: ReadonlyArray<{ id: string; gid: number; kind: TriggerKey; d: number; p?: number; title?: string }> = [
  { id: 'e1', gid: 1962700, kind: 'ea', d: 2 },
  { id: 'e2', gid: 108600, kind: 'upd', d: 4, title: 'Build 43' },
  { id: 'e3', gid: 413150, kind: 'sale', d: 5, p: 8.99 },
  { id: 'e4', gid: 105600, kind: 'sale', d: 6, p: 4.99 },
  { id: 'e5', gid: 1794680, kind: 'dlc', d: 8 },
  { id: 'e6', gid: 553850, kind: 'sale', d: 9, p: 23.99 },
  { id: 'e7', gid: 3883570, kind: 'demo', d: 10 },
  { id: 'e8', gid: 892970, kind: 'upd', d: 12, title: 'Ashlands 1.0' },
];

/** A seeded board row with the account it was written FOR (`null` for a PARTY row). */
export interface SeedEvent extends BoardEvent {
  recipient: string | null;
}

export function seedEvents(now: number): SeedEvent[] {
  const out: SeedEvent[] = [];
  // PARTY rows: every seeded score, as the trigger on game_scores writes them.
  for (const [k, by] of Object.entries(seedScores(now))) {
    for (const [gid, rec] of Object.entries(by)) {
      out.push({ id: 'sc:' + k + ':' + gid, at: rec.at, kind: 'score', gid: Number(gid), actor: k, payload: { v: rec.v }, recipient: null });
    }
  }
  // WORLD rows: the fixture, through each account's own gate.
  const watch = seedWatch();
  for (const [k, by] of Object.entries(watch)) {
    for (const e of NB_SEED) {
      const w = by[String(e.gid)];
      if (!w) continue;
      let kind: TriggerKey | '' = '';
      if (e.kind === 'sale') {
        if (w.price && e.p !== undefined && e.p <= w.cap) kind = 'price';
        else kind = w.sale ? 'sale' : '';
      } else kind = w[e.kind] ? e.kind : '';
      if (!kind) continue;
      const payload: Record<string, unknown> =
        kind === 'price'
          ? { p: e.p, cap: w.cap }
          : kind === 'sale'
            ? { p: e.p, old: Math.round((e.p ?? 0) / 0.6 * 100) / 100 }
            : kind === 'upd'
              ? { title: e.title ?? '' }
              : kind === 'dlc'
                ? { n: 1 }
                : kind === 'date'
                  ? { ym: '2026-11' }
                  : {};
      out.push({ id: 'ev:' + k + ':' + e.id, at: now - e.d * 864e5, kind, gid: e.gid, actor: null, payload, recipient: k });
    }
  }
  return out;
}

export function seedSends(now: number): SendRec[] {
  return SEED_SENDS.map((r) => ({ id: r[0], from: r[1], to: r[2], gid: r[3], note: r[4], at: now - r[5] * 864e5, st: '' }));
}

/** Each member's shelf, shelved 30 days ago and every three days after. */
export function seedBacklog(now: number): Record<string, BacklogEntry[]> {
  const out: Record<string, BacklogEntry[]> = {};
  for (const [k, rows] of Object.entries(SEED_MINE)) {
    out[k] = rows.map((r, i) => ({ id: r[0], at: now - (30 - i * 3) * 864e5 }));
  }
  return out;
}

/** Each member's scores — the notice board's PARTY rows come from the recent ones. */
export function seedScores(now: number): Record<string, Record<string, ScoreRec>> {
  const out: Record<string, Record<string, ScoreRec>> = {};
  for (const [k, rows] of Object.entries(SEED_MINE)) {
    out[k] = {};
    for (const r of rows) if (r[1] !== null) out[k][String(r[0])] = { v: r[1], at: now - r[2] * 864e5 };
  }
  return out;
}

// ---------------------------------------------------------------------------
// playtests (prototype `ptSeed()`, line 1944)
// ---------------------------------------------------------------------------

const IV_ART = 'https://cdn.cloudflare.steamstatic.com/steam/apps/3883570/header.jpg';

const party = (mx: number, lbl?: string, vs = false) => ({ mx, lbl: lbl ?? ptPartyLbl(mx), vs });

/** UTC so a committed fixture is machine-independent (see module header). */
const utc = (y: number, mo: number, d: number) => Date.UTC(y, mo - 1, d, 12, 0, 0);

export function ptSeed(now: number): Playtest[] {
  const D = 864e5;
  return [
    {
      id: 'dow', kind: 'steam', appid: 1818140, artUrl: '', name: 'Den of Wolves', dev: '10 Chambers',
      hue: 20, party: party(4, '1-4 CO-OP'), status: 'open', ended: false, wStart: now - 2 * D, wEnd: now + 5 * D,
      foot: '', auto: false, by: 'marcus', at: now - 30 * D,
      note: '4-man heist FPS from the GTFO team. Wave 4 grants are landing in batches all week — yours showed up the second you opened the site.',
      desc: 'Co-op heist FPS from the GTFO team, set in a corporate-dystopia financial district. Access is per-account — everyone squads with their own grant.',
      url: 'https://store.steampowered.com/app/1818140/', srcLabel: '',
      datesSrc: 'SOURCE: AUTO — STORE-WIDGET SCRAPE · WINDOW CONFIRMED BY 10 CHAMBERS POST',
      int: { chelsea: now - 4 * D },
      st: {
        marcus: { s: 'in', m: 'GRANTED AUG 04 · AUTO-DETECTED' },
        jeff: { s: 'in', m: 'GRANTED AUG 04' },
        dave: { s: 'req', m: 'JUL 24 · WAITING' },
        chris: { s: 'req', m: 'JUL 30 · WAITING' },
      },
    },
    {
      id: 'sr', kind: 'steam', appid: 3837830, artUrl: '', name: 'StarRupture', dev: 'Creepy Jar',
      hue: 40, party: party(4, '1-4 CO-OP'), status: 'open', ended: false, wStart: now + 2 * D, wEnd: now + 4 * D,
      foot: '', auto: false, by: 'marcus', at: now - 20 * D,
      note: 'Co-op base-building survival. Weekend stress test announced by the dev.',
      desc: 'Co-op base-building survival on a planet that wants you dead. Request access on the store page, then mark it here.',
      url: 'https://store.steampowered.com/app/3837830/', srcLabel: '',
      datesSrc: 'SOURCE: AUTO — STORE-WIDGET SCRAPE · DATES FROM DEV ANNOUNCEMENT',
      int: { dave: now - 6 * D },
      st: {
        marcus: { s: 'req', m: 'REQUESTED JUL 30' },
        mike: { s: 'req', m: 'REQUESTED JUL 31' },
        chris: { s: 'in', m: 'IN SINCE WAVE 1' },
      },
    },
    {
      id: 'mar', kind: 'off', appid: 0, artUrl: '', name: 'Marathon', dev: 'Bungie',
      hue: 260, party: party(3, '1-3 SQUAD'), status: 'open', ended: false,
      wStart: utc(2026, 9, 15), wEnd: utc(2026, 9, 19),
      foot: '', auto: false, by: 'chelsea', at: now - 29 * D,
      note: 'Extraction-shooter alpha. Signup on Bungie.net — link your Steam ID; grants go out by email.',
      desc: 'Squad-extraction shooter alpha. Signup on Bungie.net — link your Steam ID during signup; grants arrive by email.',
      url: 'https://www.bungie.net/7/en/Marathon', srcLabel: 'BUNGIE.NET',
      datesSrc: 'DATES: BUNGIE POST, EDITED BY CHELSEA AUG 02',
      int: { mike: now - 9 * D },
      st: {
        chelsea: { s: 'req', m: 'SELF-REPORTED JUL 08' },
        dave: { s: 'req', m: 'SELF-REPORTED JUL 12' },
      },
    },
    {
      id: 'inv', kind: 'steam', appid: 0, artUrl: IV_ART, name: 'Invokyr', dev: 'Ludogram',
      hue: 320, party: party(4, '1-4 CO-OP'), status: 'open', ended: false, wStart: 0, wEnd: 0,
      foot: 'SIGNUPS OPEN', auto: true, by: '', at: now - 12 * D,
      note: "Cursed-board-game co-op horror. Playtest signups are open on Steam. On Games Tonight?, so it's tracked for everyone.",
      desc: 'Co-op horror where the board game is cursed and the table bites back. Playtest signups are open on Steam.',
      url: 'https://store.steampowered.com/app/3883570/', srcLabel: '',
      datesSrc: 'SOURCE: AUTO — STORE-WIDGET SCRAPE',
      int: { chris: now - 5 * D, dave: now - 3 * D },
      st: {
        marcus: { s: 'in', m: 'GRANTED AUG 01' },
        jeff: { s: 'in', m: 'GRANTED AUG 01' },
        chelsea: { s: 'in', m: 'GRANTED AUG 02' },
      },
    },
    {
      id: 'pro', kind: 'steam', appid: 2820740, artUrl: '', name: 'Prologue: Go Wayback', dev: 'PlayerUnknown Productions',
      hue: 120, party: party(1), status: 'open', ended: false, wStart: 0, wEnd: 0,
      foot: 'WATCHING FOR WAVES', auto: false, by: 'mike', at: now - 8 * D,
      note: "Solo survival hike-out. Signups open, no wave dates announced — we watch the playtest app's player count and post when one runs.",
      desc: 'Solo survival: hike out of procedurally-generated wilderness with what you can carry. Waves detected from playtest-app player counts.',
      url: 'https://store.steampowered.com/app/2820740/', srcLabel: '',
      datesSrc: 'SOURCE: AUTO — STORE-WIDGET SCRAPE',
      int: { mike: now - 8 * D },
      st: {},
    },
    {
      id: 'dl', kind: 'steam', appid: 1422450, artUrl: '', name: 'Deadlock', dev: 'Valve',
      hue: 200, party: party(6, '6V6', true), status: 'none', ended: false, wStart: 0, wEnd: 0,
      foot: 'NO SIGNUP PAGE', auto: false, by: 'marcus', at: now - 60 * D,
      note: '6v6 shooter-MOBA. No public signup — access spreads tester-to-tester by friend invite. Chris has 2 left; ask in Discord.',
      desc: '6v6 third-person shooter-MOBA. No public signup exists — access spreads tester-to-tester by friend invite. Status is user-asserted.',
      url: '', srcLabel: '',
      datesSrc: 'STATUS: USER-ASSERTED — NO WIDGET ON THE STORE PAGE',
      int: { marcus: now - 15 * D, jeff: now - 10 * D },
      st: { chris: { s: 'in', m: 'INVITED MAR 2026' } },
    },
    {
      id: 's2', kind: 'steam', appid: 1962700, artUrl: '', name: 'Subnautica 2', dev: 'Unknown Worlds',
      hue: 210, party: party(4, '1-4 CO-OP'), status: 'closed', ended: true, wStart: 0, wEnd: utc(2026, 5, 20),
      foot: '', auto: false, by: 'dave', at: now - 120 * D,
      note: 'Closed beta wrapped — shipped to early access in May.',
      desc: 'Closed beta wrapped — the game shipped to early access in May 2026.',
      url: 'https://store.steampowered.com/app/1962700/', srcLabel: '',
      datesSrc: 'SOURCE: AUTO — STORE-WIDGET SCRAPE',
      int: {},
      st: { marcus: { s: 'in', m: 'WAS IN WAVE 2' }, dave: { s: 'in', m: 'WAS IN WAVE 2' } },
    },
    {
      id: 'rt', kind: 'steam', appid: 0, artUrl: '', name: 'Routine', dev: 'Lunar Software',
      hue: 280, party: party(1), status: 'closed', ended: true, wStart: 0, wEnd: utc(2026, 6, 30),
      foot: '', auto: false, by: 'marcus', at: now - 90 * D,
      note: 'Wave 2 closed. Watching for the next one.',
      desc: 'Retro-future lunar horror. Wave 2 closed; the widget is gone — we re-check nightly.',
      url: '', srcLabel: '',
      datesSrc: 'SOURCE: AUTO — STORE-WIDGET SCRAPE',
      int: {},
      st: { marcus: { s: 'in', m: 'WAS IN WAVE 2' } },
    },
  ];
}
